import type { BillingOverview } from "../../shared/billing-schemas";
import { COMMERCIAL_DOCUMENT_LABELS } from "../../shared/commercial-documents";
import { HUB_RECORD_LABELS, type HubOverview } from "../../shared/tool-hubs";
import { documentMoney } from "../knowledge/customer-document-format";

export type Attention = { key: string; text: string; detail: string; href: string };

/** Everything that needs someone now, from each tool's next step and from billing (ADR-0088). */
export function attentionItems(input: {
  slug: string;
  hubs: readonly HubOverview[];
  billing: BillingOverview | undefined;
  needsPrimaryContact: boolean;
}): Attention[] {
  const base = `/customers/${input.slug}`;
  const items: Attention[] = input.hubs
    .filter((item) => item.next.rule !== "nothing_due")
    .map((item) => ({
      key: `hub:${item.hub.id}`,
      text: item.next.text,
      detail: item.hub.name,
      href: `${base}/tools/${item.hub.id}`,
    }));
  const billing = input.billing;
  if (billing && billing.pastDueCount > 0)
    items.push({
      key: "past-due",
      text: `${billing.pastDueCount} past-due ${billing.pastDueCount === 1 ? "invoice" : "invoices"}`,
      detail: `${documentMoney(billing.outstandingMinor)} open`,
      href: `${base}/billing`,
    });
  const waiting = billing?.quotes.filter((quote) => quote.status === "sent").length ?? 0;
  if (waiting > 0)
    items.push({
      key: "proposals",
      text: `${waiting} ${waiting === 1 ? "proposal is" : "proposals are"} waiting for a decision`,
      detail: "Billing",
      href: `${base}/billing`,
    });
  if (input.needsPrimaryContact)
    items.push({
      key: "primary-contact",
      text: "Choose a primary contact",
      detail: "Every customer needs one person who speaks for them",
      href: `${base}/people`,
    });
  return items;
}

export type Activity = { key: string; at: number; kind: string; text: string };

/** Newest records and contract events across every tool. */
export function recentActivity(hubs: readonly HubOverview[]): Activity[] {
  return hubs
    .flatMap((item) => [
      ...item.latestRecords.map((record) => ({
        key: record.id,
        at: record.occurredAt,
        kind: HUB_RECORD_LABELS[record.type],
        text: `${record.title} · ${item.hub.name}`,
      })),
      ...(item.documents ?? []).map((document) => ({
        key: document.id,
        at: document.updatedAt,
        kind: document.filing ? "Signed" : COMMERCIAL_DOCUMENT_LABELS[document.kind],
        text: `${document.reference} ${document.filing ? "signed and filed" : `version ${document.revision} saved`} · ${item.hub.name}`,
      })),
    ])
    .sort((a, b) => b.at - a.at)
    .slice(0, 6);
}
